// Utility to track user access and store email addresses
import { sendAccessNotification } from './emailNotifications';

interface AccessRecord {
  email: string;
  name?: string;
  accessType: 'full' | 'demo';
  timestamp: string;
}

const STORAGE_KEY = 'dynami_access_records';
const MAX_RECORDS = 1000; // Limit stored records

/**
 * Track user access to the application
 * This function:
 * 1. Stores the access record in localStorage
 * 2. Sends an email notification to the admin
 */
export async function trackAccess(
  email: string, 
  name?: string, 
  accessType: 'full' | 'demo' = 'full'
): Promise<void> {
  try {
    const record: AccessRecord = {
      email: email.toLowerCase().trim(),
      name: name?.trim(),
      accessType,
      timestamp: new Date().toISOString(),
    };

    // Get existing records
    const existingRecords = getAccessRecords();
    
    // Check if this is a new user (by email)
    const isNewUser = !existingRecords.some(r => r.email === record.email);
    
    // Add new record
    const updatedRecords = [record, ...existingRecords].slice(0, MAX_RECORDS);
    
    // Store in localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedRecords));
    
    // Log to console for development
    console.log('📊 Access tracked:', record);
    
    // Send email notification (don't await - fire and forget)
    // This won't block the user from accessing the app
    sendAccessNotification({
      ...record,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
    }).then(success => {
      if (success) {
        console.log('✅ Email notification sent successfully');
      } else {
        console.warn('⚠️ Email notification was not sent (check configuration)');
      }
    }).catch(error => {
      console.error('❌ Failed to send email notification:', error);
    });
  } catch (error) {
    console.error('Error tracking access:', error);
    // Fail silently - don't block user access if tracking fails
  }
}

/**
 * Get all access records
 */
export function getAccessRecords(): AccessRecord[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    return JSON.parse(stored) as AccessRecord[];
  } catch (error) {
    console.error('Error reading access records:', error);
    return [];
  }
}

/**
 * Get unique email count
 */
export function getUniqueEmailCount(): number {
  const records = getAccessRecords();
  const uniqueEmails = new Set(records.map(r => r.email));
  return uniqueEmails.size;
}

/**
 * Export access records as JSON (for admin use)
 */
export function exportAccessRecords(): string {
  const records = getAccessRecords();
  return JSON.stringify(records, null, 2);
}

/**
 * Clear all access records (admin function)
 */
export function clearAccessRecords(): void {
  localStorage.removeItem(STORAGE_KEY);
}


